import {
  AVATAR_IKONI_PRODUCT_ID,
  GAME_SUGGESTION_CREDIT_PRODUCT_IDS,
} from '../components/partials/profileHelpers';

/** Keep trailing pack number on the same line as the label (e.g. "Symbols 1"). */
function keepLabelAndNumberTogether(text) {
  return String(text).replace(/\s+(\d+)\s*$/, '\u00A0$1');
}

function themePackName(t, themeKey) {
  const themeLabel = keepLabelAndNumberTogether(t(themeKey));
  return `${t('shop.avatarWord')} ${t('shop.wordIcons')} - ${t('shop.themePack')}: ${themeLabel}`;
}

function unlockName(t, subjectKey) {
  return `${t('shop.unlockPrefix')} ${t(subjectKey)}`;
}

/** credit_products.id → localized display title (DB product_name is fallback). */
const PRODUCT_RENDERERS = {
  [AVATAR_IKONI_PRODUCT_ID]: (t) =>
    `${t('shop.unlockPrefix')} ${t('shop.avatarWord')} ${t('shop.wordIcons')}`,
  'tier1-unlock-statistics': (t) => unlockName(t, 'shop.unlock.statistics'),
  'tier1-avatar-horoskoopit': (t) => themePackName(t, 'shop.theme.horoskoopit'),
  'tier1-unlock-arvoitus': (t) => unlockName(t, 'shop.unlock.riddleGame'),
  'tier1-unlock-emojit': (t) => unlockName(t, 'shop.unlock.emojiGame'),
  'tier1-ehdota-arvoitus': (t) => t('shop.suggest.riddle'),
  'tier1-ehdota-trivia': (t) => t('shop.suggest.triviaQuestion'),
  'tier1-ehdota-arvoitusta-l3': (t) => t('shop.suggest.riddle'),
  'tier3-unlock-advanced-statistics': (t) => unlockName(t, 'shop.unlock.advancedStatistics'),
  'tier2-unlock-abcd': (t) => unlockName(t, 'shop.unlock.abcdGame'),
  'tier2-avatar-pelit-1': (t) => themePackName(t, 'shop.theme.games1'),
  'tier2-avatar-symbolit-1': (t) => themePackName(t, 'shop.theme.symbols1'),
  'tier2-avatar-ruoka-1': (t) => themePackName(t, 'shop.theme.food1'),
  'tier2-avatar-elaimet-1': (t) => themePackName(t, 'shop.theme.animals1'),
  'tier2-mainosta-tapahtuma': (t) => t('shop.promote.yourEvent24h'),
  'tier2-mainosta-postaus': (t) => t('shop.promote.yourPost'),
  'tier2-ehdota-emoji-kuva': (t) => t('shop.suggest.emojiImageQuestion'),
  'tier2-paivan-kysymys': (t) => t('shop.dailyQuestion.billboard'),
  'tier3-paivan-kysymys': (t) => t('shop.dailyQuestion.billboard'),
  'tier2-ehdota-abcd': (t) => t('shop.suggest.abcdQuestion'),
  'tier2-korosta-kysymys-kortti': (t) => t('shop.buy.highlightQuestionCard'),
  'tier3-lisaa-kappale-jonoon': (t) => t('shop.addSongToPlaylist'),
  'tier3-unlock-levyraati': (t) => unlockName(t, 'shop.unlock.recordReview'),
  'tier4-unlock-levyraati': (t) => unlockName(t, 'shop.unlock.recordReview'),
  'tier3-avatar-pelit-2': (t) => themePackName(t, 'shop.theme.games2'),
  'tier3-avatar-symbolit-2': (t) => themePackName(t, 'shop.theme.symbols2'),
  'tier3-avatar-ruoka-2': (t) => themePackName(t, 'shop.theme.food2'),
  'tier3-avatar-elaimet-2': (t) => themePackName(t, 'shop.theme.animals2'),
  'tier3-creators-media-kit': (t) => t('shop.creatorsMediaKit'),
  'tier3-ehdota-mainosgrafiikka': (t) => t('shop.suggest.adGraphics'),
  'tier3-osta-kysymyskortti': (t) => t('shop.buy.questionCard'),
  'tier3-kysymyspatteri': (t) => t('shop.questionPattern.billboard'),
  'tier3-unlock-game-host': (t) => t('shop.unlock.gameHostMode'),
  'tier4-unlock-game-host': (t) => t('shop.unlock.gameHostMode'),
  'tier3-private-event-rooms': (t) => unlockName(t, 'shop.privateEventRooms'),
  'tier3-creator-promo': (t) => t('shop.creatorPromo.billboard'),
  'tier3-unlock-ristiseiska': (t) => unlockName(t, 'shop.unlock.sevenCrosses'),
  'tier4-mainosta-postaus-1vk': (t) => t('shop.promote.yourPost1wk'),
  'tier4-mainosta-tapahtuma-1vk': (t) => t('shop.promote.yourEvent1wk'),
  'tier4-paivan-kysymys-1vk': (t) => t('shop.dailyQuestion.billboard1wk'),
  'tier4-creator-promo-1vk': (t) => t('shop.creatorPromo.billboard1wk'),
};

/** Localized credit-shop product title (DB product_name is fallback only). */
export function creditShopProductDisplayName(product, t) {
  const id = String(product?.id ?? '').trim();
  const renderer = id ? PRODUCT_RENDERERS[id] : null;
  if (renderer) return renderer(t);

  if (
    GAME_SUGGESTION_CREDIT_PRODUCT_IDS.has(id) &&
    (id.includes('emoji') || id.includes('emoji-kuva'))
  ) {
    return t('shop.suggest.emojiImageQuestion');
  }
  if (id === 'tier2-ehdota-abcd' || (GAME_SUGGESTION_CREDIT_PRODUCT_IDS.has(id) && id.includes('abcd'))) {
    return t('shop.suggest.abcdQuestion');
  }

  const dbName = String(product?.productName ?? '').toLowerCase();
  if (/ehdota.*emoji|emoji.*kuvakysymy/i.test(dbName)) {
    return t('shop.suggest.emojiImageQuestion');
  }
  if (/ehdota.*abcd|abcd.*kysymy/i.test(dbName)) {
    return t('shop.suggest.abcdQuestion');
  }
  if (id === 'tier2-korosta-kysymys-kortti' || /korosta.*kysymy/i.test(dbName)) {
    return t('shop.buy.highlightQuestionCard');
  }
  if (id === 'tier3-osta-kysymyskortti' || /kysy.*liven|kysymyskortti/i.test(dbName)) {
    return t('shop.buy.questionCard');
  }
  if (id === 'tier3-kysymyspatteri' || /kysymyspatteri|kysely.*1\s*vk/i.test(dbName)) {
    return t('shop.questionPattern.billboard');
  }
  if (
    id === 'tier3-private-event-rooms' ||
    /yksityiset.*tapahtumahuone|private.*event.*room/i.test(dbName)
  ) {
    return unlockName(t, 'shop.privateEventRooms');
  }
  if (
    id === 'tier3-lisaa-kappale-jonoon' ||
    id === 'tier2-lisaa-kappale-jonoon' ||
    /lisää.*kappale.*soittojono|add.*song.*(playlist|queue|streaming)/i.test(dbName)
  ) {
    return t('shop.addSongToPlaylist');
  }

  return product?.productName ?? '';
}
